class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestVersion = 0;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', () => {
      this.closeResults();
      if (this.getQuery()) this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.onChange();
      }, 300);
    });
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.closeResults();
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.closeResults();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  closeResults() {
    clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.requestVersion += 1;
    this.abortController?.abort();
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.resetButton.classList.remove('btn--loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.clearSelection();
    document.body.classList.remove('predictive-search-open');
  }

  clearSelection() {
    this.input.removeAttribute('aria-activedescendant');
    this.querySelectorAll('[role="option"][aria-selected="true"]').forEach((option) => {
      option.setAttribute('aria-selected', 'false');
      option.style.outline = '';
      option.style.outlineOffset = '';
    });
  }

  onKeyDown(event) {
    if (event.isComposing) {
      if (event.key === 'Escape') this.escapeInComposition = true;
      return;
    }
    if (event.key === 'Escape' && !event.repeat) {
      this.escapeClosedResults = false;
      this.escapeInComposition = false;
    }
    if (event.key === 'Escape' && (this.hasAttribute('results') || this.hasAttribute('loading') || this.searchTimer)) {
      this.escapeClosedResults = true;
      event.preventDefault();
      event.stopPropagation();
      this.input.focus();
      this.closeResults();
      return;
    }
    if (event.target !== this.input || event.isComposing) return;
    if (event.key === 'Enter') {
      const selected = this.querySelector('[role="option"][aria-selected="true"] a');
      if (selected) {
        event.preventDefault();
        selected.click();
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !this.hasAttribute('results')) return;
    const options = Array.from(this.querySelectorAll('[role="option"]'));
    if (!options.length) return;
    event.preventDefault();
    const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1)
      : (current + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
    this.clearSelection();
    options[next].setAttribute('aria-selected', 'true');
    options[next].style.outline = '2px solid currentColor';
    options[next].style.outlineOffset = '2px';
    this.input.setAttribute('aria-activedescendant', options[next].id);
    options[next].scrollIntoView({ block: 'nearest' });
  }

  onKeyUp(event) {
    if (event.key === 'Escape' && (this.escapeClosedResults || this.escapeInComposition || event.isComposing)) {
      // The drawer also listens for Escape on keyup; dismiss suggestions first.
      event.stopPropagation();
      this.escapeClosedResults = false;
      this.escapeInComposition = false;
    }
  }

  async renderSection(url) {
    this.abortController?.abort();
    const version = ++this.requestVersion;
    const query = this.getQuery();
    this.abortController = new AbortController();
    this.setAttribute('loading', 'true');
    this.removeAttribute('error');
    this.resetButton.classList.add('btn--loading');
    try {
      let responseText = this.cachedMap.get(url);
      if (!responseText) {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('Network response was not ok');
        responseText = await response.text();
        this.cachedMap.set(url, responseText);
      }
      if (version !== this.requestVersion || query !== this.getQuery() || !query) return;
      if (this.renderSearchResults(responseText)) {
        this.setAttribute('results', 'true');
        this.input.setAttribute('aria-expanded', 'true');
        if (this.closest('.template-search')) document.body.classList.add('predictive-search-open');
      }
    } catch (error) {
      if (version !== this.requestVersion || error.name === 'AbortError') return;
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', 'false');
      this.setAttribute('error', 'Failed to load data');
      console.error('Error fetching data: ', error);
    } finally {
      if (version === this.requestVersion) {
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
      }
    }
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        this.clearSelection();
        targetElement.innerHTML = contentElement.innerHTML;
        return true;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      this.closeResults();
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
